import type { StyleSpecification } from "maplibre-gl";
import { useEffect, useRef } from "react";
import MapGL, { type MapRef, Marker } from "react-map-gl/maplibre";

// OSM raster basemap — no API key required. Kept at module scope so the style
// reference is stable across renders and the map isn't re-initialized.
const OSM_STYLE: StyleSpecification = {
	version: 8,
	sources: {
		osm: {
			type: "raster",
			tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
			tileSize: 256,
			attribution: "© OpenStreetMap contributors",
		},
	},
	layers: [{ id: "osm", type: "raster", source: "osm" }],
};

// Brazil centroid + low zoom for the "no coordinates yet" state.
const BRAZIL_VIEW = { longitude: -51.925, latitude: -14.235, zoom: 3 };
const PIN_ZOOM = 15;
// Street-level zoom for a geocoded address that has no confirmed pin yet — close
// enough to recognize the block, loose enough that the camera move is honest
// about being approximate.
const CENTER_ZOOM = 16;

interface AddressMapProps {
	lat?: number;
	lng?: number;
	// Approximate point from geocoding the typed address. Moves the camera so the
	// user can click the exact spot; it never becomes the saved pin on its own.
	center?: { lat: number; lng: number };
	onChange: (lat: number, lng: number) => void;
}

export function AddressMap({ lat, lng, center, onChange }: AddressMapProps) {
	const mapRef = useRef<MapRef>(null);
	const hasCoords = typeof lat === "number" && typeof lng === "number";

	// Recenter when the coordinates change from outside (CEP autofill) so the
	// pin stays in view. initialViewState is left uncontrolled so panning the
	// map doesn't fight React state.
	useEffect(() => {
		if (hasCoords && mapRef.current) {
			mapRef.current.flyTo({
				center: [lng as number, lat as number],
				zoom: PIN_ZOOM,
				duration: 800,
			});
		}
	}, [lat, lng, hasCoords]);

	// Center on a freshly geocoded address. `center` only changes on an explicit
	// geocode (CEP autofill without coords, or the "center on address" button),
	// so moving the camera is always a deliberate user action — even when a pin
	// already exists, jumping to the typed address helps the user re-place it.
	useEffect(() => {
		if (center && mapRef.current) {
			mapRef.current.flyTo({
				center: [center.lng, center.lat],
				zoom: CENTER_ZOOM,
				duration: 800,
			});
		}
	}, [center]);

	return (
		<div className="address-map">
			<MapGL
				ref={mapRef}
				mapStyle={OSM_STYLE}
				initialViewState={
					hasCoords
						? { longitude: lng, latitude: lat, zoom: PIN_ZOOM }
						: BRAZIL_VIEW
				}
				onClick={(event) => onChange(event.lngLat.lat, event.lngLat.lng)}
				style={{ position: "absolute", inset: 0 }}
			>
				{hasCoords && (
					<Marker
						longitude={lng as number}
						latitude={lat as number}
						draggable
						onDragEnd={(event) => onChange(event.lngLat.lat, event.lngLat.lng)}
					/>
				)}
			</MapGL>
			{!hasCoords && (
				<p className="address-map__hint">
					{center
						? "Clique no mapa para marcar o ponto exato"
						: "Clique no mapa para marcar a localização"}
				</p>
			)}
		</div>
	);
}
